/**
 * tapDiagnostic.js — Privacy-safe telemetry helper for the Tap trial modal.
 *
 * TEMPORARY. Used to debug a production "Field required" error emitted by
 * the Tap Card SDK v2 tokenize step. This module produces a small
 * SHAPE-ONLY payload (event name, Tap error code, description, missing
 * field name, and typeof metadata) and fires it, best-effort, at the
 * backend diagnostic sink.
 *
 * ABSOLUTE PRIVACY RULES:
 *   - Never send card number, CVV/CVC, expiry, cardholder name.
 *   - Never send token strings, ids, secrets, e-mail, phone, addresses.
 *   - Sensitive keys are dropped entirely (not redacted) so their presence
 *     is not confirmed in the log stream.
 *   - Values of non-allowlisted keys are replaced with a typeof-style
 *     descriptor, never the original value.
 *
 * Delete this file once the "Field required" root cause is confirmed.
 */

// Exact (case-insensitive) sensitive keys — for short unambiguous names
// like "id" / "name" where substring matching would over-match
// ("isValid", "id_field_name").
const SENSITIVE_EXACT = new Set([
  "id",
  "name",
  "iban",
  "pin",
  "otp",
  "code_verifier",
]);

// Any key whose lower-cased form CONTAINS one of these is dropped entirely.
const SENSITIVE_SUBSTRINGS = [
  "card",
  "pan",
  "number",
  "cvv",
  "cvc",
  "expiry",
  "expiration",
  "month",
  "year",
  "holder",
  "cardholder",
  "nameoncard",
  "first",
  "last",
  "middle",
  "fullname",
  "given",
  "family",
  "email",
  "phone",
  "mobile",
  "contact",
  "address",
  "street",
  "city",
  "postal",
  "zip",
  "country",
  "dob",
  "birth",
  "password",
  "secret",
  "apikey",
  "api_key",
  "authorization",
  "cookie",
  "token",
  "session",
];

// Keys whose PRIMITIVE value is safe to keep verbatim. Everything else is
// replaced with a describe() metadata string.
const VALUE_ALLOWLIST = new Set([
  "event",
  "phase",
  "code",
  "error_code",
  "reason",
  "type",
  "description",
  "message",
  "msg",
  "field",
  "field_name",
  "loc",
  "path",
  "status",
  "http_status",
  "browser",
  "sdk_version",
  "hostname",
  "environment",
  "env",
  "attempt",
  "invoked",
  "reached",
  "elapsed_ms",
  "length",
  "isValid",
  "valid",
  "ok",
]);

const MAX_KEYS = 40;
const MAX_ARRAY_LEN = 20;
const MAX_STRING_LEN = 200;
const MAX_DEPTH = 6;

function isSensitiveKey(key) {
  if (typeof key !== "string") return true;
  const k = key.toLowerCase();
  if (SENSITIVE_EXACT.has(k)) return true;
  return SENSITIVE_SUBSTRINGS.some((s) => k.includes(s));
}

function describe(v) {
  if (v === null) return "null";
  if (v === undefined) return "undefined";
  if (Array.isArray(v)) return `array[len=${v.length}]`;
  if (typeof v === "string") return `string(len=${Math.min(v.length, MAX_STRING_LEN)})`;
  if (typeof v === "object") return `object[keys=${Object.keys(v).length}]`;
  return typeof v;
}

export function sanitize(value, depth = 0) {
  if (depth >= MAX_DEPTH) return describe(value);

  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    const capped = value.slice(0, MAX_ARRAY_LEN).map((v) => sanitize(v, depth + 1));
    if (value.length > MAX_ARRAY_LEN) capped.push(`__truncated__+${value.length - MAX_ARRAY_LEN}`);
    return capped;
  }

  if (typeof value === "object") {
    const out = {};
    const keys = Object.keys(value);
    for (let i = 0; i < keys.length; i++) {
      if (i >= MAX_KEYS) {
        out.__truncated__ = `+${keys.length - i} keys`;
        break;
      }
      const k = keys[i];
      if (isSensitiveKey(k)) continue;
      const v = value[k];
      if (v !== null && typeof v === "object") {
        out[k] = sanitize(v, depth + 1);
      } else if (VALUE_ALLOWLIST.has(k)) {
        if (typeof v === "string") out[k] = v.slice(0, MAX_STRING_LEN);
        else if (typeof v === "boolean" || typeof v === "number" || v === null) out[k] = v;
        else out[k] = describe(v);
      } else {
        out[k] = describe(v);
      }
    }
    return out;
  }

  if (typeof value === "string") return value.slice(0, MAX_STRING_LEN);
  return value;
}

/**
 * Fire-and-forget POST to /billing/tap/diagnostic. Never throws, never
 * blocks the caller, and never returns a rejected promise.
 *
 * @param {string} event   Short label like "onError" / "onSuccess" / "tokenize.invoke".
 * @param {object} [data]  Arbitrary JSON — will be recursively sanitized.
 */
export function postDiagnostic(event, data) {
  try {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${backend}/api/billing/tap/diagnostic`;

    const payload = {
      event: String(event || "unknown").slice(0, 64),
      data: sanitize(data || {}),
    };

    // Prefer sendBeacon for reliable delivery on page unload / redirect.
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        if (navigator.sendBeacon(url, blob)) return;
      } catch {
        // fall through to fetch
      }
    }

    if (typeof fetch === "function") {
      // keepalive lets the request survive a redirect to Tap's 3DS page.
      fetch(url, {
        method: "POST",
        credentials: "omit",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => {});
    }
  } catch {
    // Diagnostic must never crash the modal.
  }
}
